import React, {ReactNode} from 'react';

import {darken, lighten} from 'polished';
import {useTranslation} from 'react-i18next';
import SelectComponent, {Props as SelectProps, OptionProps, components} from 'react-select';
import {makeAsyncSelect} from 'react-select/async';
import Creatable from 'react-select/creatable';
import styled, {css} from 'styled-components';

import {ReactComponent as ArrowButtonIcon} from '../../../assets/svg/arrow-bottom.svg';
import {I18N_NS} from '../../../core/constants/names';
import {border, colors, inputFieldBase, InputFieldBaseType, textEllipsis} from '../../../theme';

import {Icon} from '../Icon';
import {Label} from '../Label';

export interface SelectOption<A> {
    label: ReactNode;
    value: A | null;
}

interface StyledSelectProps {
    isDisabled?: boolean;
    hasError?: boolean;
    size?: 'sm' | 'md';
}

const DropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
        <Icon source={ArrowButtonIcon} width={10} height={6} />
    </components.DropdownIndicator>
);

interface Props extends SelectProps {
    label?: string;
    hasError?: boolean;
    disabled?: boolean;
    formFieldName?: string;
    optionComponent?: (props: OptionProps<any, false>) => JSX.Element;
    size?: 'sm' | 'md';
}

export const Select = React.forwardRef<SelectComponent, Props>((props, ref) => {
    const {t} = useTranslation(I18N_NS);
    return (
        <Label text={props.label} hasError={props.hasError} isRequired={props.isRequired}>
            <CustomSelectComponent
                classNamePrefix="react-select"
                placeholder={t('components.select.chooseOption')}
                loadingMessage={() => `${t('components.select.Loading')}'...'`}
                noOptionsMessage={() => t('components.select.NotFound')}
                tabSelectsValue={false}
                isDisabled={props.disabled}
                openMenuOnFocus
                ref={ref}
                components={{
                    DropdownIndicator,
                    ...(props.optionComponent && {Option: props.optionComponent}),
                }}
                hideSelectedOptions={false}
                {...props}
            />
        </Label>
    );
});

export const AsyncSelect = makeAsyncSelect(Select);

export const CreatableSelect = React.forwardRef<SelectComponent, Props>((props, ref) => {
    const {t} = useTranslation(I18N_NS);
    return (
        <Label text={props.label} hasError={props.hasError} isRequired={props.isRequired}>
            <CustomCreatableComponent
                classNamePrefix="react-select"
                placeholder={t('components.select.chooseOption')}
                loadingMessage={() => `${t('components.select.Loading')}'...'`}
                noOptionsMessage={() => t('components.select.NotFound')}
                tabSelectsValue={false}
                isDisabled={props.disabled}
                openMenuOnFocus
                ref={ref}
                components={{DropdownIndicator}}
                {...props}
            />
        </Label>
    );
});

const getStyles = (type: InputFieldBaseType, size?: 'sm' | 'md') => css`
    width: 100%;

    .react-select {
        &__control {
            ${textEllipsis};
            ${inputFieldBase(type, size)};
            cursor: pointer;
            min-height: ${size === 'sm' ? '36px' : '44px'};
            padding: ${size === 'sm' ? '2px 6px' : '6px 12px'};
            ${size === 'sm' &&
                css`
                    height: 30px;
                `}

            &--is-focused {
                box-shadow: none;
            }

            &--is-disabled {
                background: #f7f7f7;
                .react-select__dropdown-indicator {
                    color: ${colors.textMuted};
                }
                .react-select__placeholder {
                    color: ${colors.darkGrey};
                }
            }
        }

        &__single-value--is-disabled {
            color: ${colors.darkGrey};
        }

        &__placeholder {
            color: ${colors.textSecondary};
        }
        &__value-container {
            padding: ${size === 'sm' ? '2px 6px' : 0};

            > :last-child {
                margin: 0;
                padding: 0;
            }

            &--is-multi {
                width: 0;
                height: 100%;
                flex-wrap: nowrap;
                gap: ${size === 'sm' ? '3px' : '5px'};
            }
        }

        &__dropdown-indicator {
            color: ${colors.textPrimary};
            padding: 0;

            &:hover {
                color: ${colors.textPrimary};
            }

            svg {
                height: 14px;
            }
        }

        &__clear-indicator {
            color: ${colors.textPrimary};
            padding: 0;
            svg {
                height: ${size === 'sm' ? '12px' : '14px'};
            }
        }

        &__indicator-separator {
            display: none;
        }

        &__menu {
            margin: 0;
            &-list {
                padding: 4px;
                max-height: 360px;
            }
        }

        &__multi-value {
            display: flex;
            align-items: center;
            height: 100%;
            overflow: hidden;
            margin: 0;
            border-radius: ${border.radius};
            color: ${colors.brandPrimary};
            background: ${colors.brandLight};

            &__label {
                color: ${colors.brandPrimary} !important;
                font-size: 14px;
                font-weight: 600;
            }

            &__remove {
                border-radius: 0 ${border.radius} ${border.radius} 0;
                transition: all 0.15s ease-in-out;
                height: 100%;

                & > svg {
                    width: 15px;
                    height: 20px;
                    fill: ${colors.brandPrimary};
                }

                &:hover,
                &:focus {
                    background: ${colors.brandPrimary};
                    color: ${colors.brandLight};
                    & > svg {
                        fill: ${colors.brandLight};
                    }
                }
            }
        }

        &__option {
            cursor: pointer;
            padding: ${size === 'sm' ? '6px 10px' : '10px 15px'};
            font-size: 12px;
            background-color: ${colors.white};
            border-radius: ${border.radius};
            border-bottom: 1px solid ${colors.border};
            color: ${colors.textPrimary};

            &:last-child {
                border: 0;
            }

            &--is-selected {
                color: ${colors.white};
                background-color: ${colors.brandPrimary};
            }

            &--is-focused {
                &:not(.react-select__option--is-selected) {
                    background-color: ${lighten(0.35, colors.brandPrimary)};
                }
                &.react-select__option--is-selected {
                    background-color: ${darken(0.05, colors.brandPrimary)};
                }
            }
        }
    }
`;

const CustomSelectComponent = styled(SelectComponent)<StyledSelectProps>`
    ${(p) => getStyles(p.isDisabled ? 'disabled' : p.hasError ? 'error' : 'normal', p.size)};
`;

const CustomCreatableComponent = styled(Creatable)<StyledSelectProps>`
    ${(p) => getStyles(p.isDisabled ? 'disabled' : p.hasError ? 'error' : 'normal', p.size)};
`;

// const CreatableSelectWrapper = React.forwardRef<any, any>((props, ref) => {
//     const {t} = useTranslation(I18N_NS);
//     return (
//         <Creatable
//             classNamePrefix="react-select"
//             placeholder={t('components.select.chooseOption')}
//             tabSelectsValue={false}
//             openMenuOnFocus
//             ref={ref}
//             {...props}
//         />
//     );
// });
//
// export const CreatableSelect = styled(CreatableSelectWrapper)<{hasError?: boolean}>`
//     ${styles};
//
//     ${(p) =>
//         p.hasError &&
//         css`
//             .react-select {
//                 &__control {
//                     border-color: ${colors.danger};
//
//                     &:hover {
//                         border-color: ${colors.danger};
//                     }
//
//                     &--is-focused {
//                         border-color: ${colors.danger};
//
//                         &:hover {
//                             border-color: ${colors.danger};
//                         }
//                     }
//                 }
//             }
//         `};
// `;
